import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, message, Select, Typography, Modal, Descriptions } from 'antd';
import { LoginOutlined, LogoutOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext';
import { rtdb } from '../firebase';
import { ref, onValue } from 'firebase/database';

const { Header } = Layout;
const { Text } = Typography;

const AppHeader = () => {
  const [, setLocation] = useLocation();
  const {
    currentUser,
    logout,
    userGreenhouses,
    selectedGreenhouse,
    selectGreenhouse
  } = useAuth();

  const [greenhouseInfo, setGreenhouseInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleLogin = useCallback(() => {
    setLocation('/signin');
  }, [setLocation]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      message.success('Déconnexion réussie');
      setLocation('/signin');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      message.error('Erreur lors de la déconnexion');
    }
  }, [logout, setLocation]);

  const handleGreenhouseChange = useCallback((value) => {
    selectGreenhouse(value);
    message.info(`Serre sélectionnée: ${value}`);
  }, [selectGreenhouse]);

  useEffect(() => {
    if (!selectedGreenhouse) {
      return;
    }

    const infoRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/informations`);

    const unsubscribe = onValue(infoRef, (snapshot) => {
      if (snapshot.exists()) {
        setGreenhouseInfo(snapshot.val());
      } else {
        setGreenhouseInfo(null);
      }
    }, (error) => {
      console.error('Error loading greenhouse info:', error);
    });

    return () => unsubscribe();
  }, [selectedGreenhouse]);

  const buildModalItems = () => {
    if (!greenhouseInfo) return [];
    const items = [];

    if (greenhouseInfo.name) {
      items.push({ key: 'name', label: 'Nom', children: greenhouseInfo.name });
    }
    if (greenhouseInfo.adress) {
      items.push({ key: 'adress', label: 'Adresse', children: greenhouseInfo.adress });
    }
    if (greenhouseInfo.location?.name) {
      items.push({ key: 'location', label: 'Lieu', children: greenhouseInfo.location.name });
    }

    if (greenhouseInfo.serre) {
      const s = greenhouseInfo.serre;
      const dims = [];
      if (s.length_m) dims.push(`Longueur: ${s.length_m} m`);
      if (s.width_m) dims.push(`Largeur: ${s.width_m} m`);
      if (s.height_m) dims.push(`Hauteur: ${s.height_m} m`);
      if (s.area_m2) dims.push(`Surface: ${s.area_m2} m²`);
      if (dims.length) {
        items.push({
          key: 'dimensions',
          label: 'Dimensions serre',
          children: <ul className="gh-modal-list">{dims.map((d, i) => <li key={i}>{d}</li>)}</ul>
        });
      }
    }



    if (greenhouseInfo.nutriments) {
      const n = greenhouseInfo.nutriments;
      const parts = [];
      if (n.marque) parts.push(n.marque);
      if (n.prix_l) parts.push(`${n.prix_l} €/L`);
      if (parts.length) {
        items.push({ key: 'nutriments', label: 'Nutriments', children: parts.join(' — ') });
      }
    }

    return items;
  };

  return (
    <>
      <Header className="app-header">
        <div className="header-top-row">
          <div className="header-logo" onClick={() => setLocation('/')}>
            <span className="header-logo-bio">Bio</span>
            <span className="header-logo-connect">Connect</span>
          </div>

          <div className="header-user">
            {currentUser ? (
              <Button
                type="text"
                className="header-user-btn"
                onClick={handleLogout}
              >
                <span className="header-user-email">{currentUser.email}</span>
                <LogoutOutlined className="header-user-icon" />
              </Button>
            ) : (
              <Button
                type="primary"
                ghost
                size="small"
                className="header-login-btn"
                icon={<LoginOutlined />}
                onClick={handleLogin}
              >
                Connexion
              </Button>
            )}
          </div>
        </div>

        {currentUser && userGreenhouses.length > 0 && (
          <div className="header-greenhouse">
            <Select
              value={selectedGreenhouse || undefined}
              onChange={handleGreenhouseChange}
              size="small"
              variant="borderless"
              popupMatchSelectWidth={false}
              className="header-gh-select"
              placeholder="Serre"
            >
              {userGreenhouses.map(greenhouseId => (
                <Select.Option key={greenhouseId} value={greenhouseId}>
                  {greenhouseId}
                </Select.Option>
              ))}
            </Select>

            {selectedGreenhouse && greenhouseInfo?.name && (
              <Text
                className="header-gh-name"
                onClick={() => setModalOpen(true)}
                ellipsis={{ tooltip: greenhouseInfo.name }}
              >
                {greenhouseInfo.name}
              </Text>
            )}
          </div>
        )}
      </Header>

      <Modal
        title={
          <span>
            <InfoCircleOutlined style={{ marginRight: 8, color: '#3696ca' }} />
            {greenhouseInfo?.name || selectedGreenhouse}
          </span>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={480}
        className="gh-info-modal"
      >
        <Descriptions
          column={1}
          size="small"
          bordered
          items={buildModalItems()}
        />
      </Modal>
    </>
  );
};

export default AppHeader;
